/**
 * #388: the shared core for reading and retiring hat-customisation bundles in the cart.
 *
 * WHY THIS FILE EXISTS. A customised hat is added as one bundle group PER HAT, tagged with a shared
 * `_bundleId`, and Cart Transform merges each group into a bundle line. Shopify then COLLAPSES identical
 * bundle lines into a single display line carrying the real count, which is what makes the quantity field
 * honest. The cost is that the visible line is N independently addressable groups, so any code that
 * replaces or removes a bundle has to deal with all N.
 *
 * Two surfaces need that logic. `cart-bundle-edit.js` drives the Qty and Size selects on the cart page and
 * in the drawer; `hat-customiser.js` retires the line an EDIT session is replacing, on the product page.
 * The subtleties below are easy to get wrong in a way that silently loses or duplicates a buyer's hats, and
 * duplicating them in two files is how the two copies drift apart, so they live here once.
 */
window.CartBundleGroups = (function () {
  'use strict';

  /* Matches BUNDLE_MAX_QTY in cart-bundle-edit.js and the select's own option range. */
  const MAX_GROUPS = 10;

  /*
   * The cart line budget, and the reason it exists.
   *
   * Quantity multiplies cart lines, because every hat in a bundle is its own set of them. Past roughly 85
   * lines the cart transform stops running, and a transform that fails has ALL of its operations discarded,
   * so nothing merges at all: the buyer's hat and every accessory turn into separate cart lines with their
   * own quantity controls and remove links.
   *
   * Measured twice, either side of the function's query being trimmed.
   *
   * BEFORE (staging 24 Aug): 80 merged, 88 and 90 did not, and the shape was irrelevant -- at 90 lines it
   * failed identically as 45 groups of 2, 30 of 3, and 9 of 10. A purely per-line cost.
   *
   * AFTER the trim deployed: the ceiling moved up, and the shape started to matter. At our own worst case
   * of quantity 10, 90 lines merge and 100 do not. But 90 lines FAIL as 18 groups of 5 while merging as 9
   * of 10, and 18 groups of 2 are fine at 36 lines. So both the line count and the group count contribute
   * and neither alone explains it. The exact mechanism is not established.
   *
   * 80 STAYS, deliberately, rather than being raised to the newly measured 88 or 90:
   *
   *   - The real limit is bytes, not lines, and these measurements used short titles like "VIC State Pin".
   *     A catalogue name such as "Akubra CBR Replacement Bands (Rough Rider - Black / 53)" is four times
   *     that, so a cart of the same line count can be far larger and fail sooner.
   *   - What raising buys is thin: it would let a hat with exactly eight accessories reach 9 instead of 8.
   *   - What it costs if wrong is a cart that silently falls apart into loose lines, which is the defect
   *     this budget exists to prevent.
   *
   * The trim's value is headroom against that byte variance and against future fields being added to the
   * query, not extra quantity. Raise it only against a real order that needs it, and re-measure with that
   * store's actual product names.
   */
  const MAX_CART_LINES = 80;

  /** How many bundles of `linesPerBundle` lines each can be held without breaking the transform. */
  function maxUnitsFor(linesPerBundle) {
    const per = Math.max(1, linesPerBundle || 1);
    return Math.max(1, Math.min(MAX_GROUPS, Math.floor(MAX_CART_LINES / per)));
  }

  /*
   * A SHORT, HUMAN-READABLE group code, because this ends up in front of the warehouse.
   *
   * It was a UUID. On the packing screen every component of every hat prints its own line tagged
   * `_bundleId: 8988c491-88c3-4bb6-86dc-31765af2a150`, and a picker matching a feather to the right hat has
   * to compare 36 characters by eye across a dozen lines. Five characters can be read at a glance.
   *
   * The alphabet drops 0/O, 1/I/L and vowels: no ambiguous glyphs to mis-read off a screen or a printed
   * sheet, and no accidental words. 5 chars from 26 symbols is about 12 million codes, and these only need
   * to be unique within one cart, so a collision is not a practical concern.
   *
   * Still `_`-prefixed, and the key is unchanged. The cart transform's input query matches on `_bundleId`
   * exactly, so renaming the KEY would need the extension redeployed in lockstep and a mismatch stops all
   * merging; only the VALUE is shortened here.
   */
  function newGroupCode() {
    const alphabet = '23456789BCDFGHJKMNPQRSTVWXYZ';
    const size = 5;
    let out = '';

    if (window.crypto && crypto.getRandomValues) {
      const bytes = new Uint8Array(size);
      crypto.getRandomValues(bytes);
      for (let i = 0; i < size; i += 1) out += alphabet[bytes[i] % alphabet.length];
      return out;
    }

    for (let i = 0; i < size; i += 1) {
      out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out;
  }

  async function getCart() {
    return (await fetch(Shopify.routes.root + 'cart.js', { cache: 'no-store' })).json();
  }

  async function postJSON(url, body) {
    const response = await fetch(Shopify.routes.root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const detail = await response.json().catch(function () {
        return {};
      });
      throw new Error(detail.description || detail.message || 'The bag could not be updated.');
    }
    return response.json();
  }

  /*
   * A stable identity for a collapsed bundle line, because the KEY is not one: it re-hashes as groups are
   * added and removed, so a key captured before the first removal is stale by the second.
   *
   * The parent variant id alone is too broad, since every customised hat of one product merges to the same
   * parent and it would also match a differently configured bundle sitting beside this one. `Includes` is
   * the attribute the cart transform writes listing the components and the size, so the pair tells "4 hats
   * with 2 pins" apart from "1 hat with 2 pins in another size" while still matching every group inside the
   * one line.
   *
   * SORTED, because the transform builds `Includes` by mapping over the cart lines in cart order and that
   * order changes as groups are removed. Measured 23 Aug on a 4-hat line: before the first removal it read
   * "Tablelands Sand (53), 2 x VIC State Pin" and immediately after, "2 x VIC State Pin, Tablelands Sand
   * (53)". An unsorted signature therefore stopped matching after one removal and the caller's loop exited
   * having cleared a single group, which is the partial removal all of this exists to prevent.
   *
   * Splitting on the bare comma rather than ", " is deliberate: a component title containing a comma splits
   * into more fragments, but the SAME fragments every time, so sorting still yields one stable signature.
   */
  function signatureOf(item) {
    const parentVariant = String(item.key || '').split(':')[0];
    const includes = (item.properties && item.properties.Includes) || '';
    const parts = includes
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean)
      .sort()
      .join('|');

    return parentVariant + '|' + parts;
  }

  /**
   * Retires EVERY group behind one collapsed cart line, one call each.
   *
   * No single call can do it. Measured on a 4-hat line: `{id: key, quantity: 0}` leaves 3,
   * `{line: 1, quantity: 0}` leaves 3, and `{id: key, quantity: 2}` leaves 3 plus a line reporting 1 at
   * double price, which is the pre-per-hat pathology resurfacing on the one group it did reach.
   *
   * Bounded by MAX_GROUPS rather than written as a `while`, so a signature that somehow keeps matching
   * cannot spin the cart forever. The normal path exits early, the moment nothing matches.
   *
   * Throws on a stale key rather than returning quietly. Callers remove BEFORE they add, so a quiet return
   * removes nothing and then duplicates the bundle; observed as a 4-hat line becoming 4 + 2 and billing
   * six. Throwing lets the caller restore its control and leave the cart untouched.
   */
  async function removeAll(lineKey) {
    const cart = await getCart();
    let line = cart.items.find(function (item) {
      return item.key === lineKey;
    });

    if (!line) {
      throw new Error('This bag line is out of date. Refresh the page and try again.');
    }

    const signature = signatureOf(line);
    for (let attempt = 0; attempt < MAX_GROUPS && line; attempt += 1) {
      await postJSON('cart/change.js', { id: line.key, quantity: 0 });
      const now = await getCart();
      line = now.items.find(function (item) {
        return signatureOf(item) === signature;
      });
    }
  }

  /**
   * Builds the add.js `items` for `units` hats of one configuration.
   *
   * One independent `_bundleId` per hat, because the transform merges by that id and N distinct ids yield N
   * bundle lines for Shopify to collapse into a line reporting N. Component quantities are PER HAT and are
   * not multiplied: each group describes one hat.
   */
  function buildItems(hatVariantId, components, units) {
    /*
     * Clamped by what fits as well as by MAX_GROUPS. One hat contributes 1 + its accessories in cart lines,
     * so a ten-accessory hat is capped near eight rather than ten. Enforced here, at the single point every
     * caller goes through, rather than trusting each control to have capped its own input.
     */
    const linesPerBundle = 1 + ((components || []).length);
    const ceiling = maxUnitsFor(linesPerBundle);
    const count = Math.min(ceiling, Math.max(1, parseInt(units, 10) || 1));
    const items = [];

    for (let unit = 0; unit < count; unit += 1) {
      const bundleId = newGroupCode();

      items.push({ id: hatVariantId, quantity: 1, properties: { _bundleId: bundleId } });

      (components || []).forEach(function (component) {
        items.push({
          id: component.id,
          quantity: component.quantity || 1,
          // Spread first so a stray key cannot overwrite _bundleId.
          properties: Object.assign({}, component.properties, { _bundleId: bundleId }),
        });
      });
    }

    return items;
  }

  return {
    MAX_GROUPS: MAX_GROUPS,
    MAX_CART_LINES: MAX_CART_LINES,
    maxUnitsFor: maxUnitsFor,
    getCart: getCart,
    postJSON: postJSON,
    signatureOf: signatureOf,
    newGroupCode: newGroupCode,
    removeAll: removeAll,
    buildItems: buildItems,
  };
})();
